import {TrackingHeadScript} from '@phntms/next-gtm';
import * as Sentry from '@sentry/react';
import Providers from 'components/providers/Providers';
import {Scripts} from 'components/scripts';
import {RouteLoader} from 'components/shared';
import useOneSignal from 'hooks/useOneSignal';
import type {NextPage} from 'next';
import type {AppProps} from 'next/app';
import dynamic from 'next/dynamic';
import localFont from 'next/font/local';
import {ReactElement, ReactNode} from 'react';
import logger from 'utils/logger';

export const montserrat = localFont({
  src: [
    {
      path: '../../public/fonts/montserrat-v31-cyrillic_latin-500.woff2',
      weight: '500',
      style: 'normal',
    },
    {
      path: '../../public/fonts/montserrat-v31-cyrillic_latin-600.woff2',
      weight: '600',
      style: 'normal',
    },
    {
      path: '../../public/fonts/montserrat-v31-cyrillic_latin-700.woff2',
      weight: '700',
      style: 'normal',
    },
  ],
  variable: '--font-montserrat',
  display: 'swap',
});

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const CookieConsentBanner = dynamic(() => import('components/shared/CookieConsentBanner'), {
  ssr: false,
});

function MyApp({Component, pageProps}: AppPropsWithLayout) {
  useOneSignal();
  logger.logDebug('rerender', 'MyApp');

  return (
    <Sentry.ErrorBoundary
      onError={(error: unknown, componentStack, eventId) => {
        const err = error instanceof Error ? error : new Error(String(error));
        logger.error(err, {stack: componentStack, err, eventId});
      }}
      fallback={<h1>A client-side exception has occurred!!!</h1>}
    >
      <Providers {...pageProps}>
        <Scripts />
        <TrackingHeadScript id="GTM-PS9NN85" isGTM={true} />
        <RouteLoader />
        <Component {...pageProps} />
        <CookieConsentBanner />
      </Providers>
    </Sentry.ErrorBoundary>
  );
}

export default MyApp;
